import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isJsonArray, isJsonObject, Json, IconType } from '@se/cp.common';
import { CpExamination } from '@se/models';
import {
    BoolIconRendererComponent,
    ButtonClick,
    CpOpenableExamination,
    CpRouterDatabuilderService,
    EstensaWaitingService,
    ExaminationsFilter,
    fromServerIsoString,
    getSePatientName,
    getSePatientSurname,
    GridSelectionModel,
    GridUtilitiesService,
    IReportWorkflowExamService,
    isTooMuchResultFault,
    ModalDialogNotificationService,
    NotificationType,
    REPORT_WORKFLOW_EXAM_SERVICE_TOKEN,
} from '@se/platform';
import { themeEstensa } from '@se/se.platform.base';
import { AgGridAngular } from 'ag-grid-angular';
import { AllCommunityModule, ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import moment from 'moment';
import { finalize, firstValueFrom, Observable } from 'rxjs';

export interface AddableExamination extends CpOpenableExamination {
    examinationDate: string;
    patient: {
        id: string;
        firstName: string;
        lastName: string;
    };
    exam: {
        id: string;
        description: string | null;
    };
}

export interface AddExamDialogData {
    enableSearch: boolean;
    header: string;
    examination: AddableExamination;
    selectionModel: GridSelectionModel;
    status?: boolean;
    fnSave?: () => Observable<({ model: Json } & Action<'[Cp] SetModel'>) | Action>;
}

@Component({
    selector: 'se-add-exam',
    templateUrl: './add-exam.component.html',
    styleUrls: ['./add-exam.component.scss'],
    standalone: false,
})
export class AddExamComponent {
    @ViewChild('datagrid', { static: true })
    grid?: AgGridAngular;

    items: CpExamination[] = [];
    loading = false;
    message = '';
    gridOptions = this.buildGridOptions();
    examToSearch: ExaminationsFilter = {
        name: this.data.examination.patient.firstName.toUpperCase(),
        surname: this.data.examination.patient.lastName.toUpperCase(),
        date: this.data.examination.examinationDate,
        idPatient: this.data.examination.patient.id,
        idCustomPageRoot: this.data.examination.idCustomPageRoot,
    };
    agGridModuls = [AllCommunityModule];
    agGridTheme = themeEstensa;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: AddExamDialogData,
        @Inject(REPORT_WORKFLOW_EXAM_SERVICE_TOKEN) private examService: IReportWorkflowExamService,
        private cpRouterDatabuilderService: CpRouterDatabuilderService,
        private dialogRef: MatDialogRef<AddExamComponent, string[]>,
        private gridUtils: GridUtilitiesService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translate: TranslateService,
        private waitingService: EstensaWaitingService,
    ) {}

    private async onAddWhenNotReported(): Promise<void> {
        const fnSave = this.data.fnSave;
        if (!fnSave) return this.dialogRef.close();
        const saving = await this.askToSave();
        if (!saving) {
            return this.dialogRef.close();
        } else {
            this.waitingService.isWaiting = true;
            const result = (await firstValueFrom(fnSave())) as {
                model: Json;
            } & Action<'[Cp] SetModel'>;
            if (isJsonObject(result.model) && result.model.Id) {
                await this.onAdd(modelToAddableExamination(result.model));
            }
            this.waitingService.isWaiting = false;
        }
    }

    private async askToSave(): Promise<boolean> {
        const buttonClick = await firstValueFrom(
            this.modalDialogNotificationService.notify({
                message: this.translate.instant('AddExamWarning'),
                title: 'Warning',
                type: NotificationType.warning,
                buttonYes: true,
                buttonNo: true,
                buttonOk: false,
                buttonCancel: false,
            }),
        );

        return buttonClick === ButtonClick.yes;
    }

    private async onAdd(examination?: AddableExamination) {
        const examinationsToAdd = this.grid?.api?.getSelectedRows();
        if (!examinationsToAdd) {
            return;
        }
        this.waitingService.isWaiting = true;

        const allExams = examination
            ? examinationsToAdd.concat(examination)
            : examinationsToAdd.concat(this.data.examination);

        try {
            const cpArguments = await this.cpRouterDatabuilderService.buildCpArguments(
                allExams,
                this.examService,
                false,
            );
            if (!cpArguments || !cpArguments.idCustomPageRoot) {
                return;
            }
        } finally {
            this.waitingService.isWaiting = false;
        }

        const confirmation = await this.askForConfirmation(examinationsToAdd);
        if (!confirmation) {
            return;
        }

        this.waitingService.isWaiting = true;
        const idExaminations = examinationsToAdd.map((e) => e.id);
        this.examService
            .addExaminationsToReport?.({
                idReport: examination ? examination.idReport : this.data.examination.idReport,
                idExaminations,
            })
            .pipe(
                finalize(() => {
                    this.waitingService.isWaiting = false;
                    this.onClose(idExaminations);
                }),
            )
            .subscribe();
    }

    async _checkAddFnToUse() {
        if (this.data.status) {
            return await this.onAddWhenNotReported();
        } else {
            return await this.onAdd();
        }
    }

    onGridReady($event: GridReadyEvent<any, any>) {
        $event.api?.sizeColumnsToFit();

        this.onSearch();
    }

    private async askForConfirmation(examinations: CpExamination[]): Promise<boolean> {
        const message =
            (examinations.length == 1
                ? this.translate.instant('Visit_AddExamConfirm_1')
                : this.translate.instant('Visit_AddExamConfirm_0')) +
            ' ' +
            examinations
                .map((ex) => {
                    const patient = ex.patient;
                    const name = patient.firstName || patient.name || '';
                    const surname = patient.lastName || patient.surname || '';

                    const visitPatientMsg =
                        surname.toUpperCase() +
                        ' ' +
                        name.toUpperCase() +
                        ' (' +
                        fromServerIsoString(patient.birthDate ?? '')
                            .toDate()
                            .toLocaleDateString() +
                        ')';
                    return (
                        ex.exam.description +
                        ' ' +
                        this.translate.instant('Visit_AddExamConfirm_2') +
                        ' ' +
                        visitPatientMsg
                    );
                })
                .join(', ') +
            ' ' +
            this.translate.instant('Visit_AddExamConfirm_3');

        const buttonClick = await firstValueFrom(
            this.modalDialogNotificationService.notify({
                buttonYes: true,
                buttonNo: true,
                buttonCancel: false,
                buttonOk: false,
                title: this.translate.instant('Warning'),
                message,
                type: NotificationType.warning,
            }),
        );

        return buttonClick === ButtonClick.yes;
    }

    onClose(addedIdExaminations?: string[]) {
        this.loading = false;
        this.message = '';
        this.items = [];
        this.examToSearch = { idCustomPageRoot: '' };
        this.dialogRef.close(addedIdExaminations);
    }

    onSearch() {
        this.loading = true;
        let searchFilter: ExaminationsFilter = {
            name: this.examToSearch.name,
            surname: this.examToSearch.surname,
            idPatient: this.examToSearch.idPatient,
            idCustomPageRoot: this.data.examination.idCustomPageRoot,
        };
        if (this.examToSearch.date) {
            searchFilter.date = moment(this.examToSearch.date).utc().toDate();
        }
        this.examService.searchExaminationsToAddToReport?.(searchFilter).subscribe({
            next: (items) => {
                this.loading = false;
                this.items = items.filter((i) => i.id !== this.data.examination.id);
            },
            error: (exception) => {
                this.loading = false;
                this.message = this.lookupErrorMessage(exception);
                this.items = [];
            },
        });
    }

    onResetDate() {
        this.examToSearch.date = null;
    }

    canAdd(): boolean {
        return (this.grid?.api?.getSelectedRows().length ?? 0) > 0 && !this.loading;
    }

    private lookupErrorMessage(exception: unknown) {
        if (!(exception instanceof HttpErrorResponse)) {
            return this.translate.instant('Exception_GenericFault');
        }

        if (isTooMuchResultFault(exception.error)) {
            return this.translate.instant('Exception_TooMuchResultFault', {
                p0: exception.error.estensaFaultDetails.estensaFault.count,
                p1: exception.error.estensaFaultDetails.estensaFault.limit,
                p2: ' ',
            });
        }

        if ('message' in exception) {
            return this.translate.instant('Exception_GenericFault') + ' ' + exception.message;
        }
    }

    private buildGridOptions(): GridOptions {
        return {
            rowSelection:
                this.data.selectionModel === GridSelectionModel.single
                    ? {
                          mode: 'singleRow',
                      }
                    : { mode: 'multiRow' },
            suppressCellFocus: true,
            columnDefs: this.buildColumns(),
            defaultColDef: { editable: false, sortable: true, resizable: true },
            enableBrowserTooltips: true,
            components: {
                boolIconRenderer: BoolIconRendererComponent,
            },
        };
    }

    private buildColumns(): ColDef[] {
        if (this.data.enableSearch) {
            return this.buildPatientColumns().concat(this.buildExamColumns());
        }

        return this.buildExamColumns();
    }

    private buildExamColumns(): ColDef[] {
        return [
            {
                colId: '2',
                headerName: this.translate.instant('ExamDate'),
                headerTooltip: this.translate.instant('ExamDate'),
                tooltipField: 'examinationDate',
                field: 'examinationDate',
                width: 135,
                minWidth: 120,
                valueFormatter: this.gridUtils.dateTimeFormatter('examinationDate'),
                comparator: this.gridUtils.dateComparator,
            },
            {
                colId: '3',
                headerName: this.translate.instant('Exam'),
                headerTooltip: this.translate.instant('Exam'),
                tooltipField: 'exam.description',
                field: 'exam.description',
                width: 220,
                minWidth: 190,
            },
            {
                colId: '4',
                headerName: this.translate.instant('Status'),
                headerTooltip: this.translate.instant('Status'),
                tooltipField: 'status',
                field: 'status',
                width: 100,
                minWidth: 100,
            },
            {
                colId: '5',
                headerName: this.translate.instant('BusinessUnit'),
                headerTooltip: this.translate.instant('BusinessUnit'),
                tooltipField: 'businessUnit.description',
                field: 'businessUnit.description',
                width: 220,
                minWidth: 190,
            },
        ];
    }

    private buildPatientColumns(): ColDef[] {
        return [
            {
                colId: 'ExamOutOfFlow',
                headerName: '',
                tooltipField: '',
                field: 'patient.isLocalPatient',
                width: 50,
                suppressSizeToFit: true,
                resizable: false,
                lockPosition: true,
                pinned: 'left',
                cellStyle: { padding: '0', display: 'flex', justifyContent: 'center', marginLeft: '5px' },
                cellRenderer: 'boolIconRenderer',
                cellRendererParams: {
                    estensa: {
                        iconType: IconType.SeFont,
                        icon: 'icon_flusso_inverso',
                        tooltip: 'PatientDataNotAssociated',
                    },
                },
            },
            {
                colId: '0',
                headerName: this.translate.instant('Patient'),
                headerTooltip: this.translate.instant('Patient'),
                valueGetter: (e) =>
                    `${getSePatientSurname(e.data.patient).toLocaleUpperCase()} ${getSePatientName(e.data.patient).toLocaleUpperCase()}`,
                tooltipValueGetter: (e) =>
                    `${getSePatientSurname(e.data.patient).toLocaleUpperCase()} ${getSePatientName(e.data.patient).toLocaleUpperCase()}`,
                width: 200,
                minWidth: 190,
                suppressSizeToFit: true,
                lockPosition: true,
                pinned: 'left',
                cellStyle: () => {
                    return null;
                },
            },
            {
                colId: '1',
                headerName: this.translate.instant('BirthDate'),
                headerTooltip: this.translate.instant('BirthDate'),
                tooltipField: 'patient.birthDate',
                field: 'patient.birthDate',
                width: 155,
                minWidth: 120,
                valueFormatter: this.gridUtils.dateFormatter(['patient', 'birthDate']),
                comparator: this.gridUtils.dateComparator,
            },
        ];
    }
}

function modelToAddableExamination(model: unknown): AddableExamination {
    if (
        !isJsonObject(model) ||
        !isJsonObject(model.ReferenceExamination) ||
        !isJsonObject(model.Patient) ||
        !isJsonArray(model.IdModelTypeRootList)
    ) {
        throw new Error('Model is required');
    }

    return {
        exam: {
            id: '',
            description: '',
        },
        examinationDate: model.ReferenceExamination.ExaminationDate?.toString() ?? '',
        id: model.ReferenceExamination.Id?.toString() ?? '',
        idReport: model.Id?.toString() ?? '',
        idCustomPage: model.IdCustomPage?.toString() ?? '',
        idCustomPageRoot: model.IdCustomPageRoot?.toString() ?? '',
        idModelTypeRoots: model.IdModelTypeRootList.map((item) => item?.toString() ?? ''),
        isApproved: !!model.ReferenceExamination.IsApproved,
        patient: {
            id: model.Patient.Id?.toString() ?? '',
            firstName: model.Patient.FirstName?.toString() ?? '',
            lastName: model.Patient.LastName?.toString() ?? '',
        },
    };
}
